import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useStorageUpload, uploadedFileFields } from '../../lib/upload'
import { formatDate } from '../../lib/format'
import type { CourseAllocation, PhysicalTraining } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, GradCapIcon, PlusIcon } from '../../components/icons'

interface TrainingResponse { ok: boolean; physical: PhysicalTraining[]; allocations: CourseAllocation[] }
interface OptionsResponse { ok: boolean; members: { uid: string; name?: string }[]; courses: { id: string; title?: string }[] }

type Tab = 'physical' | 'allocations'

export default function Training() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('physical')
  const [showForm, setShowForm] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [selectedAttendees, setSelectedAttendees] = useState<Set<string>>(new Set())
  const upload = useStorageUpload(`training_proof/${user?.org_id ?? ''}`)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'training'],
    queryFn: () => api.get<TrainingResponse>('/ops/training'),
  })
  const optionsQuery = useQuery({
    queryKey: ['ops', 'training', 'options'],
    queryFn: () => api.get<OptionsResponse>('/ops/training/options'),
    enabled: showForm,
  })

  const recordPhysical = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/ops/training/physical', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'training'] })
      setShowForm(false); setSelectedAttendees(new Set())
    },
  })
  const allocate = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/ops/training/allocate', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'training'] })
      setShowForm(false); setSelectedAttendees(new Set())
    },
  })

  const members = optionsQuery.data?.members ?? []
  const courses = optionsQuery.data?.courses ?? []

  function toggleAttendee(uid: string) {
    setSelectedAttendees((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid); else next.add(uid)
      return next
    })
  }

  async function onSubmitPhysical(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const attendees = members.filter((m) => selectedAttendees.has(m.uid))
                              .map((m) => ({ uid: m.uid, name: m.name }))
    if (!attendees.length) return
    const file = fileRef.current?.files?.[0]
    let fileFields: Record<string, string> = {}
    if (file) {
      try {
        fileFields = uploadedFileFields(await upload.upload(file), 'proof')
      } catch {
        return
      }
    }
    recordPhysical.mutate({
      subject: form.get('subject'),
      conducted_at: form.get('conducted_at'),
      trainer: form.get('trainer'),
      objectives: form.get('objectives'),
      notes: form.get('notes'),
      attendees,
      ...fileFields,
    })
  }

  function onSubmitAllocate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const courseId = String(form.get('course_id') ?? '')
    const course = courses.find((c) => c.id === courseId)
    const members_ = members.filter((m) => selectedAttendees.has(m.uid))
                              .map((m) => ({ uid: m.uid, name: m.name }))
    if (!courseId || !members_.length) return
    allocate.mutate({
      course_id: courseId,
      course_name: course?.title ?? '',
      members: members_,
      pass_mark: Number(form.get('pass_mark') ?? 70),
      due_date: form.get('due_date'),
    })
  }

  const physical = data?.physical ?? []
  const allocations = data?.allocations ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Training</h1>
          <p className="page-head__sub">Record physical training sessions and allocate courses to members.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => { setShowForm((s) => !s); setSelectedAttendees(new Set()) }}>
          {showForm ? 'Cancel' : tab === 'physical' ? 'Record session' : 'Allocate course'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => { setShowForm((s) => !s); setSelectedAttendees(new Set()) }}
          aria-label={showForm ? 'Cancel' : tab === 'physical' ? 'Record session' : 'Allocate course'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'physical' ? 'primary' : 'secondary'} size="sm"
                onClick={() => { setTab('physical'); setShowForm(false) }}>
          Physical sessions
        </Button>
        <Button variant={tab === 'allocations' ? 'primary' : 'secondary'} size="sm"
                onClick={() => { setTab('allocations'); setShowForm(false) }}>
          Course allocations
        </Button>
      </div>

      {showForm && tab === 'physical' && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Record a physical training session" />
          <CardBody>
            <form onSubmit={onSubmitPhysical}>
              <div className="stack">
                {recordPhysical.isError && (
                  <Alert tone="danger">
                    {recordPhysical.error instanceof ApiError ? recordPhysical.error.message : 'Could not record that session.'}
                  </Alert>
                )}
                {upload.error && <Alert tone="danger">{upload.error}</Alert>}
                {/* PWA: 4 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Session', content: (
                      <div className="grid grid--2">
                        <Input label="Subject" name="subject" required />
                        <Input label="Conducted on" name="conducted_at" type="date" required />
                      </div>
                    ) },
                    { title: 'Trainer & proof', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Input label="Trainer" name="trainer" />
                          <Input label="Proof (optional)" type="file" ref={fileRef} accept="image/*,application/pdf" />
                        </div>
                        {upload.progress !== null && <div className="field__hint">Uploading… {upload.progress}%</div>}
                      </div>
                    ) },
                    { title: 'Details', content: (
                      <div className="stack">
                        <Textarea label="Objectives" name="objectives" />
                        <Textarea label="Notes" name="notes" />
                      </div>
                    ) },
                    { title: 'Attendees', content: (
                      <AttendeePicker members={members} selected={selectedAttendees} onToggle={toggleAttendee}
                                      loading={optionsQuery.isLoading} />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={recordPhysical.isPending || upload.progress !== null}
                              disabled={!selectedAttendees.size}>
                        Record session
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {showForm && tab === 'allocations' && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Allocate a course to members" />
          <CardBody>
            <form onSubmit={onSubmitAllocate}>
              <div className="stack">
                {allocate.isError && (
                  <Alert tone="danger">
                    {allocate.error instanceof ApiError ? allocate.error.message : 'Could not allocate that course.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Course', content: (
                      <Select label="Course" name="course_id" required defaultValue="" disabled={optionsQuery.isLoading}>
                        <option value="" disabled>Select a course…</option>
                        {courses.map((c) => <option key={c.id} value={c.id}>{c.title || c.id}</option>)}
                      </Select>
                    ) },
                    { title: 'Terms', content: (
                      <div className="grid grid--2">
                        <Input label="Pass mark (%)" name="pass_mark" type="number" min="0" max="100" defaultValue={70} />
                        <Input label="Due date" name="due_date" type="date" />
                      </div>
                    ) },
                    { title: 'Members', content: (
                      <AttendeePicker members={members} selected={selectedAttendees} onToggle={toggleAttendee}
                                      loading={optionsQuery.isLoading} label="Members" />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={allocate.isPending} disabled={!selectedAttendees.size}>
                        Allocate
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title={tab === 'physical' ? 'Physical sessions' : 'Course allocations'} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : tab === 'physical' ? (
            physical.length === 0 ? (
              <EmptyState icon={<GradCapIcon size={22} />} title="No sessions recorded"
                          body="Physical training sessions will appear here." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Subject</th><th>Conducted</th><th>Trainer</th><th className="table__num">Attendees</th></tr></thead>
                  <tbody>
                    {physical.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>{p.subject || '—'}</td>
                        <td>{formatDate(p.conducted_at)}</td>
                        <td>{p.trainer || '—'}</td>
                        <td className="table__num">{p.attendee_count ?? p.attendees?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : allocations.length === 0 ? (
            <EmptyState icon={<GradCapIcon size={22} />} title="No allocations yet"
                        body="Courses allocated to members will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Course</th><th>Member</th><th>Due</th><th className="table__num">Progress</th><th>Status</th></tr></thead>
                <tbody>
                  {allocations.map((a) => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 600 }}>{a.course_name || '—'}</td>
                      <td>{a.member_name || '—'}</td>
                      <td>{a.due_date ? formatDate(a.due_date) : '—'}</td>
                      <td className="table__num">{a.progress_pct ?? 0}%</td>
                      <td><StatusBadge status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function AttendeePicker({
  members, selected, onToggle, loading, label = 'Attendees',
}: {
  members: { uid: string; name?: string }[]
  selected: Set<string>
  onToggle: (uid: string) => void
  loading?: boolean
  label?: string
}) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      {loading ? (
        <Skeleton h={80} />
      ) : members.length === 0 ? (
        <div className="field__hint">No organisation members found.</div>
      ) : (
        <div style={{
          maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: 'var(--sp-3)',
        }}>
          {members.map((m) => (
            <label key={m.uid} style={{
              display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
              padding: 'var(--sp-1) 0', fontSize: 'var(--text-sm)', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={selected.has(m.uid)} onChange={() => onToggle(m.uid)} />
              {m.name || m.uid}
            </label>
          ))}
        </div>
      )}
      <span className="field__hint">{selected.size} selected</span>
    </div>
  )
}
